/**
 * 공모사업 API 서비스
 * 백엔드 공모사업 API와 통신
 */

// API Base URL 동적 감지
const getApiBaseUrl = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${protocol}//${hostname}:8001`;
  }
  return 'http://localhost:8001';
};

// 인증 헤더 생성
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('proposalcraft_access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// 공모사업 인터페이스
export interface PublicGrant {
  id: string;
  title: string;
  source: string;
  source_name?: string;
  ministry?: string;
  manage_agency?: string;
  deadline?: string;
  expected_date?: string;
  project_year?: string;
  d_day?: number;
  budget?: string;
  category?: string;
  eligibility?: string[];
  grant_type?: string;
  status: string;
  description?: string;
  url?: string;
  match_score?: number;
  is_bookmarked: boolean;
  is_notified: boolean;
  crawled_at?: string;
}

export interface GrantListResponse {
  total: number;
  items: PublicGrant[];
  crawled_at?: string;
}

export interface GrantStats {
  total: number;
  active: number;
  urgent: number;
  bookmarked: number;
  by_source?: Record<string, number>;
  by_category?: Record<string, number>;
}

export interface CrawlResponse {
  success: boolean;
  crawled_counts: Record<string, number>;
  total: number;
  message: string;
  errors?: string[];
  crawled_at: string;
}

// 공모사업 서비스 클래스
class GrantService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  // API URL 갱신 (원격/로컬 전환 시)
  private getUrl(path: string): string {
    return `${getApiBaseUrl()}/api/public-grants${path}`;
  }

  /**
   * 공모사업 목록 조회
   */
  async getGrants(params: {
    source?: string;
    status?: string;
    category?: string;
    search?: string;
    bookmarked_only?: boolean;
    skip?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  } = {}): Promise<GrantListResponse> {
    const queryParams = new URLSearchParams();

    if (params.source && params.source !== 'all') {
      queryParams.append('source', params.source);
    }
    if (params.status && params.status !== 'all') {
      queryParams.append('status', params.status);
    }
    if (params.category && params.category !== 'all') {
      queryParams.append('category', params.category);
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.bookmarked_only) {
      queryParams.append('bookmarked_only', 'true');
    }
    if (params.skip !== undefined) {
      queryParams.append('skip', params.skip.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.sort_by) {
      queryParams.append('sort_by', params.sort_by);
    }
    if (params.sort_order) {
      queryParams.append('sort_order', params.sort_order);
    }

    const url = this.getUrl(`/?${queryParams.toString()}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`공모사업 목록 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 공모사업 상세 조회
   */
  async getGrant(grantId: string): Promise<PublicGrant> {
    const url = this.getUrl(`/${grantId}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`공모사업 상세 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 공모사업 통계 조회
   */
  async getStats(): Promise<GrantStats> {
    const url = this.getUrl('/stats/summary');
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`통계 조회 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 북마크 토글
   */
  async toggleBookmark(grantId: string): Promise<{ success: boolean; is_bookmarked: boolean }> {
    const url = this.getUrl(`/${grantId}/bookmark`);
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`북마크 토글 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 알림 설정 토글
   */
  async toggleNotification(grantId: string): Promise<{ success: boolean; is_notified: boolean }> {
    const url = this.getUrl(`/${grantId}/notification`);
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`알림 설정 토글 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 크롤링 실행 (관리자 전용)
   */
  async triggerCrawl(options: {
    force?: boolean;
    sources?: string[];
  } = {}): Promise<CrawlResponse> {
    const queryParams = new URLSearchParams();
    
    if (options.force) {
      queryParams.append('force', 'true');
    }
    if (options.sources && options.sources.length > 0) {
      queryParams.append('sources', options.sources.join(','));
    }

    const url = this.getUrl(`/crawl?${queryParams.toString()}`);
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || `크롤링 실행 실패: ${response.status}`);
    }

    return response.json();
  }

  /**
   * 캐시 새로고침
   */
  async refreshCache(): Promise<{ success: boolean; message: string; count: number }> {
    const url = this.getUrl('/refresh');
    const response = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`캐시 새로고침 실패: ${response.status}`);
    }

    return response.json();
  }
}

// 싱글톤 인스턴스 내보내기
export const grantService = new GrantService();
export default grantService;
